import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import {
  DeleteOutline,
  EditOutlined,
  FiberManualRecordOutlined,
  SouthEastOutlined,
} from '@mui/icons-material';
import {
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useParams } from 'react-router';

import {
  blue100,
  blue200,
  blue700,
  blue900,
} from '../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import type { TAnnotateActions } from '../atoms';
import { annotateActionAtom, deleteUserAnnotationAtom } from '../atoms';

const SideControls = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  gap: 0.25em;
  padding: 0;
  position: absolute;
  top: 1em;
  left: 1.5em;
  margin: 44px 1em 0 0;
  z-index: 1;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: fit-content;
  background: ${blue700};
`;

const StyledToggleButton = styled(ToggleButton)`
  border: 0;
  border-radius: 0.25em;
  padding: 0.75em;
  color: ${blue100};
  .MuiSvgIcon-root {
    fill: ${blue200};
  }
  &.Mui-selected {
    background-color: ${blue100};
    border-radius: 0.25em;
    .MuiSvgIcon-root {
      fill: ${blue700};
    }
  }
  &.Mui-selected:hover,
  &:hover {
    background-color: ${blue200};
    .MuiSvgIcon-root {
      fill: ${blue900};
    }
  }
`;

const StyledTrashButton = styled(IconButton)`
  background: ${blue700};
  border: 0;
  border-radius: 0.15em;
  padding: 0.75em;
  color: ${blue100};
  width: 1.875em;
  height: 1.875em;
  .MuiSvgIcon-root {
    fill: ${blue200};
  }
  &.Mui-selected {
    background-color: ${blue100};
    border-radius: 0.15em;
    .MuiSvgIcon-root {
      fill: ${blue700};
    }
  }
  &.Mui-selected:hover,
  &:hover {
    background-color: ${blue200};
    .MuiSvgIcon-root {
      fill: ${blue900};
    }
  }
`;

export const ImageAnnotationTools = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const { t } = useLingui();
  const [annotateAction, setAnnotateAction] = useAtom(annotateActionAtom);
  const [shouldDeleteUserAnnotation, setShouldDeleteUserAnnotation] = useAtom(
    deleteUserAnnotationAtom,
  );
  const { trackEvent } = useTrackEvent();
  const handleSegment = (value: TAnnotateActions) => {
    setAnnotateAction(value);
    trackEvent(mixpanelEvents.casePresentationAnnotate, {
      type: value,
      patient: patientId,
    });
  };

  return (
    <SideControls>
      <StyledToggleButtonGroup
        
        value={annotateAction}
        orientation="vertical"
        exclusive
        onChange={(_, value) => handleSegment(value)}
      >
        <StyledToggleButton value="segment">
          <Tooltip title={t`draw freehand`} placement="right">
            <EditOutlined />
          </Tooltip>
        </StyledToggleButton>
        <StyledToggleButton value="circle">
          <Tooltip title={t`draw a circle`} placement="right">
            <FiberManualRecordOutlined />
          </Tooltip>
        </StyledToggleButton>
        <StyledToggleButton value="arrow">
          <Tooltip title={t`draw an arrow`} placement="right">
            <SouthEastOutlined />
          </Tooltip>
        </StyledToggleButton>
      </StyledToggleButtonGroup>
      <StyledTrashButton
        onClick={() =>
          setShouldDeleteUserAnnotation(!shouldDeleteUserAnnotation)
        }
      >
        <Tooltip title={t`clear drawings`} placement="right">
          <DeleteOutline />
        </Tooltip>
      </StyledTrashButton>
    </SideControls>
  );
};
